import { useState, ChangeEvent } from 'react';
import { useQuery } from '@apollo/client';

import usePagination from '@hooks/usePagination';
import useRangeDate from '@hooks/useRangeDate';
import { systemLogTableHeader } from '@lib/data';
import { SEE_SYSTEM_HISTORY_LIST } from '@graphql/queries';
import { Query, AdminLog } from '@graphql/types';
import { getSystemLogType } from '@lib/enum';

import { FilterBox, Contents } from '@components/styled/StyledComponents';
import Headline from '@components/layout/Headline';
import StyledSelect from '@components/styled/StyledSelect';
import StyledRangeCalendar from '@components/styled/StyledRangeCalendar';
import TableHeader from '@components/share/Table/TableHeader';
import { TableBody, EmptyTable } from '@components/share/Table/table.style';
import SystemLogTableRow from '@components/feature/Setting/SystemLog/SystemLogTableRow';
import Pagination from '@components/layout/Pagination';
import Loader from '@components/share/Loader';

const SystemLog = () => {
    const { startDate, endDate, onChangeDate } = useRangeDate();
    const [searchTerm, setSearchTerm] = useState('');
    // 페이지네이션 관련 상태
    const [take, setTake] = useState(10);
    const [preCursor, setPreCursor] = useState(0);
    const [nextCursor, setNextCursor] = useState(0);
    const [cursorOption, setCursorOption] = useState<boolean | null>(null);
    const [filter, setFilter] = useState({ order: 1, type: '전체' });

    const { data, loading } = useQuery<Pick<Query, 'seeSystemHistoryList'>>(
        SEE_SYSTEM_HISTORY_LIST,
        {
            variables: {
                orderBy: +filter.order,
                type: getSystemLogType(filter.type as string),
                searchStart: startDate,
                searchEnd: endDate,
                searchTerm,
                take,
                preCursor,
                nextCursor,
                cursorOption
            },
            onError: (err) => {
                console.error(err);
            },
            skip: !endDate
        }
    );

    const systemLogList = data?.seeSystemHistoryList?.adminLogList ?? [];
    const totalLength = data?.seeSystemHistoryList?.totalLength ?? 0;

    const onFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilter((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // 페이지네이션
    const {
        pageIndex,
        setPageIndex,
        pageGroupLength,
        prevPageHandler,
        nextPageHandler,
        prevPageGroupHandler,
        nextPageGroupHandler,
        resetPaginationHandler,
        disabled
    } = usePagination(
        take,
        totalLength,
        setPreCursor,
        setNextCursor,
        setCursorOption,
        systemLogList,
        'alId'
    );

    return (
        <Contents>
            <Headline
                title="시스템 로그"
                subTitle="펫이닷 관리자 페이지 시스템 로그"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchCount={totalLength}
                SEARCH
            />
            {/*  필터 영역 */}
            <FilterBox>
                <StyledSelect
                    title="정렬"
                    name="order"
                    value={filter.order}
                    options={[
                        { title: '내림차순', value: 1 },
                        { title: '오름차순', value: 2 }
                    ]}
                    onChange={onFilterChange}
                    DATA_VALUE
                />
                <StyledSelect
                    title="구분"
                    name="type"
                    value={filter.type}
                    options={[
                        '전체',
                        '로그인',
                        '데이터 CRUD',
                        '시스템',
                        '보안 및 접근 위험',
                        '감사 및 변경 이력'
                    ]}
                    onChange={onFilterChange}
                    width={filter.type.length * 12}
                />
                <StyledRangeCalendar
                    title="조회기간"
                    startDate={startDate}
                    endDate={endDate}
                    onChange={onChangeDate}
                    width={240}
                    ICON
                />
            </FilterBox>
            <TableHeader header={systemLogTableHeader} />

            <TableBody $minHeight={500}>
                {loading && <Loader />}
                {totalLength === 0 ? (
                    <EmptyTable>목록이 없습니다.</EmptyTable>
                ) : (
                    systemLogList
                        .slice(pageIndex, pageIndex + take)
                        .map((data, idx) => (
                            <SystemLogTableRow
                                key={`${data?.alId}-${idx}-system-log`}
                                headers={systemLogTableHeader}
                                data={data as AdminLog}
                            />
                        ))
                )}
            </TableBody>

            {totalLength > 0 && (
                <Pagination
                    take={take}
                    setTake={setTake}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    pageGroupLength={pageGroupLength}
                    totalLength={totalLength}
                    prevPageHandler={prevPageHandler}
                    nextPageHandler={nextPageHandler}
                    prevPageGroupHandler={prevPageGroupHandler}
                    nextPageGroupHandler={nextPageGroupHandler}
                    resetPaginationHandler={resetPaginationHandler}
                    disabled={disabled}
                />
            )}
        </Contents>
    );
};

export default SystemLog;
